import millify from "millify";
import ReactCountryFlag from "react-country-flag";
import UnlockDataBanner from "components/shareable/UnlockDataBanner";
import UnlockDataImage from "assets/images/unlock-data-geographies.svg";
import Loader from "elements/Loader";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { GraphTooltip } from "pages/DashboardPage/components/Graphs";
import { AxisInterval } from "recharts/types/util/types";
import { fixCountryNameForUnited, performanceTextForDemos, performanceTextForLocations } from "./helpers";
import { BarChartOutlined } from "@material-ui/icons";
import getSymbolFromCurrency from "currency-symbol-map";
import { CurrencyCodesType } from "modules/Types";
import { DEFAULT_CURRENCY, PlaybookType } from "modules/Const";
import useBrand from 'Hooks/useBrand';
import useScreen from "Hooks/useScreen";
import ButtonSymphony from 'elements/ButtonSymphony';
import Colors from "modules/Colors";
import CardSymphony from "elements/CardSymphony";
import Sty from "modules/Sty";
import { CampaignDetailsData, GeographicBreakdownItem, DemographicsBreakdownItem } from "api/campaignApi";
import BlurredGraph from "elements/BlurredGraph";
import useCampaignDetails from "Hooks/useCampaignDetails";
import DebugBox from "components/shareable/DebugBox";
import { CSSProperties } from "react";
import { capitalizeFirstLetter, clsx, styx } from "modules/Utils";

/** Valid keys for geographic region types */
type GeographicLocationType = 'regions' | 'countries' | 'dmas';

/** Mapping between geographic stats key and display name */
const GeographicAreaTypeNames: Record<GeographicLocationType, string> = {
    regions: 'Region',
    countries: 'Country',
    dmas: 'City'
}

/** Keys for campaign analytics stats */
type CampaignAnalyticsStatKey = 'clicks' | 'impressions';

/** Mapping between campaign analytics key and display name */
const CampaignAnalyticsStatNames: Record<CampaignAnalyticsStatKey, string> = {
    clicks: 'Clicks',
    impressions: 'Impressions'
}

interface DemographicsStatsProps {
    className: string,
    style?: CSSProperties,
    currency?: CurrencyCodesType,
    demographicsBreakdown: CampaignDetailsData['demographicsBreakdown'],
    geographicBreakdown: CampaignDetailsData['geographicBreakdown'],
    isLoadingDemographics: boolean,
    showMarkets?: () => void,
}

const DemographicsStats = ({
    className,
    style,
    currency,
    demographicsBreakdown,
    geographicBreakdown,
    isLoadingDemographics,
    showMarkets,
}: DemographicsStatsProps) => {
    const { campaign } = useCampaignDetails();
    const { isProBrand } = useBrand()
    const { mobileView } = useScreen()

    const maxBreakdownItems: number = 5;

    // Primary stat key used to bucket the demographic and geographic analytics
    const primaryStatKey: CampaignAnalyticsStatKey = (campaign?.campaign_metadata?.playbook !== PlaybookType.TIKTOK_BOOST_ENGAGEMENT) ? 'clicks' : 'impressions';

    // Primary stat key display name
    const primaryStatName: string = CampaignAnalyticsStatNames[primaryStatKey].toLocaleLowerCase();
    const primaryStatNameSingular = primaryStatName.replace(/s$/, '')

    const renderLocationData = (locationType: GeographicLocationType) => {
        const locationTypeData = geographicBreakdown[locationType]

        if (!locationTypeData) {
            return null
        }

        const dataMappings: Record<string, any> = {}

        const processedData = locationTypeData.filter((item: GeographicBreakdownItem) => {
            if (item.dma !== null && item.dma === 'Unknown') {
                return false
            }
            if (item.country !== null && item.country === 'None') {
                return false
            }
            return true
        })
            .map((item: GeographicBreakdownItem) => {
                let name
                let countryCode
                switch (locationType) {
                    case 'regions':
                        name = item.region
                        countryCode = null
                        break;
                    case 'countries':
                        name = item.countryName
                        countryCode = item.country
                        break
                    case 'dmas':
                        name = item.dma
                        countryCode = null
                        break;
                    default:
                        break;
                }

                if (name) {
                    dataMappings[name] = {
                        type: locationType,
                        countryCode: countryCode ? countryCode : null,
                        clicks: item.clicks,
                        cpc: item.cpc,
                        reach: item.reach,
                        spend: item.spend,
                        impressions: item.impressions
                    }
                }

                return {
                    name: name,
                    type: locationType,
                    countryCode: countryCode ? countryCode : null,
                    countryName: item.countryName ? item.countryName : null,
                    value: item[primaryStatKey],
                    clicks: item.clicks,
                    cpc: item.cpc,
                    reach: item.reach,
                    spend: item.spend,
                    impressions: item.impressions
                }
            })
            .sort((a, b) => b.value - a.value)
            .slice(0, maxBreakdownItems)

        const CustomizedAxisTick = ({
            x,
            y,
            payload
        }: any) => {
            const labelWidth = 120

            const item = dataMappings[payload.value]

            var itemName = item.type === 'countries' && mobileView ? item.countryCode : payload.value
            return (
                <g transform={`translate(${x - (labelWidth / 2)},${y}) ${mobileView ? `rotate(0 ${100 / 2} ${labelWidth / 2})` : ''}`}>
                    <foreignObject x={0} y={0} width={labelWidth} height={100} className="overflow-visible">
                        {mobileView ?
                            <div className="w-full text-center overflow-ellipses">
                                {item.countryCode && (<div >
                                    <ReactCountryFlag
                                        style={{
                                            fontSize: '0.9em'
                                        }}
                                        countryCode={item.countryCode} />
                                </div>)}
                                <div className="mt-0.5 text-sm" >{itemName}</div>
                            </div>
                            :
                            <div className="w-full text-center overflow-ellipses">
                                {item.countryCode && (<div >
                                    <ReactCountryFlag
                                        style={{
                                            fontSize: '1.1em'
                                        }}
                                        countryCode={item.countryCode} />
                                </div>)}
                                <div className="mt-0.5 text-base">{itemName}</div>
                                <div className="text-sm text-gray-500">{millify(item[primaryStatKey])} {primaryStatName}</div>
                                {primaryStatKey === 'clicks' &&
                                    <div className="text-sm text-gray-500">
                                        {getSymbolFromCurrency(currency ?? DEFAULT_CURRENCY)}{(item.cpc).toLocaleString('en', {
                                            type: 'currency',
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2
                                        })} CPC
                                    </div>
                                }
                            </div>
                        }
                    </foreignObject>
                </g>
            );
        }

        let barInterval: number | AxisInterval = 5
        if (mobileView) {
            if (locationType === 'countries') {
                barInterval = 0
            } else {
                barInterval = 1
            }
        } else {
            barInterval = 0
        }

        let emptyState = false
        if (processedData.length === 0) emptyState = true

        let firstLocation: any
        let secondLocation: any
        let subtitleSentence: string

        if (processedData.length > 2) {
            firstLocation = processedData[0].name
            secondLocation = processedData[1].name
            subtitleSentence = performanceTextForLocations([firstLocation, secondLocation], primaryStatNameSingular);
        } else if (processedData.length === 0) {
            firstLocation = null
            secondLocation = null
            subtitleSentence = `We're still waiting for data to come in for top ${locationType === 'dmas' ? 'cities' : locationType}.`
        } else {
            firstLocation = fixCountryNameForUnited(processedData[0].name!)
            subtitleSentence = `Top ${primaryStatName} came from ${firstLocation}.`
        }

        return (
            <div className="demographicsStatsCustomBox" style={{ borderBottom: Sty.border, paddingTop: 24, paddingBottom: 24 }}>
                <div className="demographicsHeaderBoxInner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <CardSymphony
                        className="demographicsStatsHeader"
                        title={`Top ${GeographicAreaTypeNames[locationType]} by ${CampaignAnalyticsStatNames[primaryStatKey]}`}
                        subtitle={subtitleSentence}
                        isNoPadding
                        size="small"
                        style={{ marginBottom: 16 }}
                        isGemIcon
                    />
                    {showMarkets && !emptyState && !mobileView &&
                        <div className="flex-shrink-0">
                            <ButtonSymphony
                                className="buttonDemographicsStats"
                                onClick={showMarkets}
                                variant="text"
                            >
                                <BarChartOutlined /> See Stats
                            </ButtonSymphony>
                        </div>
                    }
                </div>
                {!emptyState && (
                    <div>
                        <ResponsiveContainer className="mx-auto" width="100%" height={window.innerWidth < 576 ? 300 : 300} minHeight="100px">
                            <BarChart
                                width={500}
                                height={300}
                                margin={{
                                    bottom: mobileView ? 20 : 80
                                }}
                                data={processedData}
                            >
                                <XAxis type="category" dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    interval={barInterval}
                                    tick={<CustomizedAxisTick />}
                                />
                                <YAxis
                                    hide={true}
                                    domain={[(dataMin: any) => (0), (dataMax: any) => (dataMax * 1.02)]}
                                />
                                <Tooltip
                                    cursor={{ fill: Colors.purple, fillOpacity: 0.1 }}
                                    content={({ active, payload, label }) => {

                                        if (active && payload && payload.length) {
                                            let mainData: any = payload[0]
                                            if (mainData && mainData.payload) {
                                                mainData = mainData.payload
                                            } else {
                                                return null
                                            }

                                            const cpc = mainData.cpc!.toLocaleString('en', {
                                                maximumFractionDigits: 2,
                                                minimumFractionDigits: 2,
                                                type: 'currency'
                                            })

                                            const statsValue = mainData[primaryStatKey]!.toLocaleString('en', {
                                                maximumFractionDigits: 0
                                            })

                                            const spend = mainData.spend!.toLocaleString('en', {
                                                maximumFractionDigits: 0,
                                                type: 'currency'
                                            })

                                            var mainLabel = label
                                            if (locationType === 'regions') {
                                                mainLabel = `${mainLabel}`
                                            }
                                            return (
                                                <GraphTooltip
                                                    title={mainLabel}
                                                    value={(
                                                        <div>
                                                            <p className="font-semibold text-md">
                                                                {statsValue} {primaryStatName}</p>
                                                            {primaryStatKey === 'clicks' &&
                                                                <p className="font-semibold text-md">
                                                                    {getSymbolFromCurrency(currency ?? DEFAULT_CURRENCY)}{cpc} CPC
                                                                </p>
                                                            }
                                                            <p className="font-semibold text-md">
                                                                {getSymbolFromCurrency(currency ?? DEFAULT_CURRENCY)}{spend} spent
                                                            </p>
                                                        </div>
                                                    )}
                                                />
                                            );
                                        }

                                        return null;
                                    }}

                                // formatter={(value: any, name: any, props: any) => [value.toLocaleString(), "Total Fanbase Size"]} 
                                />

                                <Bar
                                    dataKey="value"
                                    fill="#C698FF"
                                    radius={6}
                                >
                                    {/* <LabelList dataKey="value" position="top"
                                    formatter={(value: number) => `${value.toLocaleString('en', {
                                        maximumFractionDigits: 0,
                                    })}%`}
                                    style={{ fill: "black" }} /> */}
                                </Bar>

                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        )
    }

    const renderDemographicsData = () => {
        if (!demographicsBreakdown) {
            return null
        }

        const dataMappings: Record<string, any> = {}

        const formattedDemographicsData = demographicsBreakdown
            .map((item: DemographicsBreakdownItem) => {
                let capitalizedGender = capitalizeFirstLetter(item.gender)
                const dataLabel = `Age ${item.age} / ${capitalizedGender}`

                dataMappings[dataLabel] = {
                    age: item.age,
                    gender: capitalizedGender,
                    name: dataLabel,
                    value: item[primaryStatKey],
                    clicks: item.clicks,
                    cpc: item.cpc,
                    spend: item.spend,
                    impressions: item.impressions
                }

                return {
                    age: item.age,
                    gender: capitalizedGender,
                    name: dataLabel,
                    value: item[primaryStatKey],
                    clicks: item.clicks,
                    cpc: item.cpc,
                    spend: item.spend,
                    impressions: item.impressions
                }
            })
            .sort((a, b) => b.value - a.value)
            .slice(0, maxBreakdownItems)

        const CustomizedDemoAxisTick = ({
            x,
            y,
            height,
            payload
        }: any) => {
            const yAxisTitle = dataMappings[payload.value]

            return (
                <text
                    x={x}
                    y={y}
                    height={height}
                    className="rechatsPersonalClassIdentifier recharts-text recharts-layer recharts-cartesian-axis-tick-value"
                    type="category"
                    orientation="left"
                    textAnchor="end"
                    style={{
                        fontSize: '0.95rem'
                    }}>
                    <tspan x={x} dy="-0.145em">{yAxisTitle.age}</tspan>
                    <tspan x={x} dy="1.3em">{yAxisTitle.gender}</tspan>
                </text>

            );
        }

        const renderCustomizedLabel = (labelProps: any) => {
            const {
                height,
                value,
                offset,
                index
            } = labelProps;

            const yValue = (index * 60) + 30

            // Get the container width for right alignment
            const containerWidth = document.querySelector('.recharts-wrapper')?.getBoundingClientRect().width || 0
            const rightPadding = 20 // Padding from right edge

            if (mobileView) {
                return (
                    <text
                        y={yValue}
                        height={height}
                        offset={offset}
                        style={{
                            fontSize: '0.8rem',
                            fill: '#000'
                        }}
                    >
                        <tspan
                            x={containerWidth - rightPadding}
                            dy="-0.145em"
                            textAnchor="end"
                        >
                            {millify(value)}
                        </tspan>
                        <tspan
                            x={containerWidth - rightPadding}
                            dy="1.2em"
                            textAnchor="end"
                        >
                            {primaryStatName}
                        </tspan>
                    </text>
                )
            }

            return (
                <text
                    x={containerWidth - rightPadding}
                    y={yValue}
                    height={height}
                    style={{
                        fontSize: '0.9rem',
                        fill: '#000'
                    }}
                    textAnchor="end"
                    dominantBaseline="middle"
                >
                    <tspan>
                        {millify(value)} {primaryStatName}
                    </tspan>
                </text>
            )
        };

        let emptyState = false
        if (formattedDemographicsData.length < 2) {
            emptyState = true
        }

        let firstSet: {
            age: string,
            gender: string
        }

        let secondSet: {
            age: string,
            gender: string
        }

        let subtitleSentence: string

        if (!emptyState) {
            firstSet = {
                age: formattedDemographicsData[0].age,
                gender: formattedDemographicsData[0].gender
            }
            secondSet = {
                age: formattedDemographicsData[1].age,
                gender: formattedDemographicsData[1].gender
            }
            subtitleSentence = performanceTextForDemos([firstSet, secondSet], primaryStatNameSingular)
        } else {
            subtitleSentence = "We're still waiting for data to come in."
        }

        const graphHeight = formattedDemographicsData.length * 60;

        return (
            <div className="topDemographicsContainer border-b py-7">
                <CardSymphony
                    className="topDemographicsHeader"
                    title={`Top Demographics by ${CampaignAnalyticsStatNames[primaryStatKey]}`}
                    subtitle={subtitleSentence}
                    isNoPadding
                    size="small"
                    style={{ marginBottom: 16 }}
                />
                {!emptyState &&
                    <ResponsiveContainer
                        className="mx-auto"
                        width="100%"
                        height={graphHeight}
                        minHeight="300px"
                    >
                        <BarChart
                            id="barChart"
                            width={500}
                            height={graphHeight}
                            // margin={{
                            //     bottom: 80
                            // }}
                            margin={{
                                left: mobileView ? 0 : 20
                            }}
                            layout="vertical"
                            data={formattedDemographicsData}
                        >
                            <XAxis
                                type="number"
                                hide
                                domain={[(dataMin: any) => (0), (dataMax: any) => (dataMax * 1.3)]}
                            />

                            <YAxis
                                type="category"
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                minTickGap={0}
                                tickMargin={10}
                                tick={<CustomizedDemoAxisTick />}
                            />

                            <Tooltip
                                cursor={{ fill: Colors.purple, fillOpacity: 0.1 }}
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        let mainData: any = payload[0]
                                        if (mainData && mainData.payload) {
                                            mainData = mainData.payload
                                        } else {
                                            return null
                                        }

                                        const cpc = mainData.cpc!.toLocaleString('en', {
                                            maximumFractionDigits: 2,
                                            minimumFractionDigits: 2,
                                            type: 'currency'
                                        })

                                        const primaryStatValue = mainData[primaryStatKey]!.toLocaleString('en', {
                                            maximumFractionDigits: 0
                                        })

                                        const spend = mainData.spend!.toLocaleString('en', {
                                            maximumFractionDigits: 0,
                                            type: 'currency'
                                        })

                                        var mainLabel = mainData.name

                                        return (
                                            <GraphTooltip
                                                title={mainLabel}
                                                value={(
                                                    <div className="demographicsTooltip">
                                                        <p className="font-semibold text-md">
                                                            {primaryStatValue} {primaryStatName}</p>
                                                        {primaryStatKey === 'clicks' &&
                                                            <p className="font-semibold text-md">
                                                                {getSymbolFromCurrency(currency ?? DEFAULT_CURRENCY)}{cpc} CPC
                                                            </p>
                                                        }
                                                        <p className="font-semibold text-md">
                                                            {getSymbolFromCurrency(currency ?? DEFAULT_CURRENCY)}{spend} spent
                                                        </p>
                                                    </div>
                                                )}
                                            />
                                        );
                                    }

                                    return null;
                                }}
                            // formatter={(value: any, name: any, props: any) => [value.toLocaleString(), "Total Fanbase Size"]} 
                            />

                            <Bar
                                dataKey="value"
                                id="bar"
                                radius={6}
                                fill="#D3B7F7"
                                background={{ fill: '#EDECF2', radius: 6 }}
                                barSize={50}
                            >
                                <LabelList
                                    dataKey="value"
                                    position="right"
                                    formatter={(value: number) => `${value.toLocaleString('en', {
                                        maximumFractionDigits: 0,
                                    })} ${primaryStatName}`}
                                    style={{ fill: "black" }}
                                    content={renderCustomizedLabel}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                }
            </div>
        )
    }

    if (isLoadingDemographics || !(geographicBreakdown && demographicsBreakdown)) {
        return (
            <div
                className="demographicsStatsLoading"
                style={{
                    margin: '24px auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Loader
                    className="demographicsStatsLoader"
                    size={44}
                    title="Loading Demographics..."
                />
            </div>
        );
    }

    if (!isProBrand) {
        return (
            <>
                <UnlockDataBanner
                    source="IG Campaign Details - Demographics"
                    title="Track your ad analytics down to the city"
                    description="Go Pro to track the cities, countries, ages, and genders of fans who interact with your ads the most."
                    image={UnlockDataImage}
                    hideImage={mobileView}
                />
                <div className="notProBrandContentContainer px-6">
                    <CardSymphony
                        className="topDemographicsHeader"
                        title="Top Countries by Clicks"
                        subtitle={`These are the top ${maxBreakdownItems} countries that clicked on your ads the most.`}
                        isNoPadding
                        size="small"
                        isGemIcon
                    />
                    <BlurredGraph
                        className="notProBrandImage"
                        graph="topCountries"
                        title="Track your ad analytics by country"
                    />
                    <CardSymphony
                        className="topDemographicsHeader"
                        title="Top Demographics by Clicks"
                        subtitle={`These are the top ${maxBreakdownItems} age & gender breakdowns that clicked on your ads the most.`}
                        isNoPadding
                        size="small"
                        isGemIcon
                    />
                    <BlurredGraph
                        className="notProBrandImage"
                        graph="topDemographics"
                        title="Track your ad analytics by age & gender"
                    />
                </div>
            </>
        );
    }

    return (
        <div
            className={clsx('demographicsStatsComponent', className)}
            style={styx(style)}
        >
            {renderDemographicsData()}
            {renderLocationData('countries')}
            {renderLocationData('regions')}
            {renderLocationData('dmas')}
        </div>
    )
}

export default DemographicsStats;